import React, { useState, useEffect } from 'react';
import { apiFetch } from '../../api/api';
import { FIELD_INPUT_OPTIONS, mapColumnTypeToFormatType } from '../../utils/formatters';
import { detectUnitCategory, getUnitCategories, getGlobalUnitSettings } from '../../utils/globalUnits';

const ColumnForm = ({ onSubmit, initialData = {}, tableName = '' }) => {
  const [name, setName] = useState(initialData.name || '');
  const [label, setLabel] = useState(initialData.label || '');
  const [unit, setUnit] = useState(initialData.unit || '');
  const [type, setType] = useState(initialData.type || 'text');
  const [options, setOptions] = useState(initialData.options || '');
  const [desc, setDesc] = useState(initialData.desc || '');
  const [link, setLink] = useState(initialData.link || '');
  
  // Lookup field configuration state
  const [lookupTable, setLookupTable] = useState(initialData.lookupTable || '');
  const [lookupValueColumn, setLookupValueColumn] = useState(initialData.lookupValueColumn || '');
  const [lookupDisplayColumn, setLookupDisplayColumn] = useState(initialData.lookupDisplayColumn || '');
  const [availableTables, setAvailableTables] = useState([]);
  const [availableColumns, setAvailableColumns] = useState([]);
  const [isLoadingTables, setIsLoadingTables] = useState(false);
  const [isLoadingColumns, setIsLoadingColumns] = useState(false);
  
  // Format-related state variables
  const [format, setFormat] = useState(initialData.format || '');
  const [textFormat, setTextFormat] = useState(initialData.textFormat || 'normal');
  const [numberFormat, setNumberFormat] = useState(initialData.numberFormat || '0.00');
  const [currencyFormat, setCurrencyFormat] = useState(initialData.currencyFormat || '$0,000.00');
  const [dateFormat, setDateFormat] = useState(initialData.dateFormat || 'DD/MM/YYYY');
  const [timeFormat, setTimeFormat] = useState(initialData.timeFormat || 'HH:mm');
  
  const [isGeneratingDesc, setIsGeneratingDesc] = useState(false);
  const [unitCategories, setUnitCategories] = useState({});
  const [globalSettings, setGlobalSettings] = useState({});
  const [detectedCategory, setDetectedCategory] = useState(null);

  // Load global units configuration
  useEffect(() => {
    const loadGlobalUnits = async () => {
      try {
        const categories = await getUnitCategories();
        const settings = await getGlobalUnitSettings();
        if (categories) setUnitCategories(categories);
        if (settings) setGlobalSettings(settings);
      } catch (error) {
        console.error('Failed to load global units configuration:', error);
      }
    };
    loadGlobalUnits();
  }, []);

  // Load available database tables for lookup configuration
  useEffect(() => {
    if (options === 'lookup') {
      loadAvailableTables();
    }
  }, [options]);

  // Load available columns when lookup table changes
  useEffect(() => {
    if (lookupTable && options === 'lookup') {
      loadAvailableColumns(lookupTable);
    }
  }, [lookupTable, options]);

  // Load available database tables
  const loadAvailableTables = async () => {
    setIsLoadingTables(true);
    try {
      const tables = await apiFetch('/api/db/tables?dbSchema=dbo');
      if (Array.isArray(tables)) {
        setAvailableTables(tables);
      }
    } catch (error) {
      console.error('Failed to load database tables:', error);
      setAvailableTables([]);
    }
    setIsLoadingTables(false);
  };

  // Load available columns for selected lookup table
  const loadAvailableColumns = async (tableName) => {
    if (!tableName) {
      setAvailableColumns([]);
      return;
    }
    
    setIsLoadingColumns(true);
    try {
      const columns = await apiFetch(`/api/db/columnlist/${tableName}?dbSchema=dbo`);
      if (Array.isArray(columns)) {
        setAvailableColumns(columns);
      }
    } catch (error) {
      console.error('Failed to load table columns:', error);
      setAvailableColumns([]);
    }
    setIsLoadingColumns(false);
  };

  // Auto-determine format based on type
  useEffect(() => {
    if (type && !format) {
      const autoFormat = mapColumnTypeToFormatType(type);
      setFormat(autoFormat);
    }
  }, [type, format]);

  // Detect unit category when name or label changes
  useEffect(() => {
    if (name || label) {
      const category = detectUnitCategory(name, label);
      setDetectedCategory(category);
    }
  }, [name, label]);

  // Auto-apply context-specific number formats
  useEffect(() => {
    if (format === 'number' && name) {
      const nameLower = name.toLowerCase();
      if (nameLower.includes('depth') || nameLower.includes('td')) {
        setNumberFormat('0,000');
      } else if (nameLower.includes('latitude') || nameLower.includes('longitude')) {
        setNumberFormat('0.0000');
      } else if (nameLower.includes('id') && type === 'int') {
        setNumberFormat('0');
      } else if (type === 'int') {
        setNumberFormat('0');
      }
    }
  }, [format, name, type]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Build the submission data with format attributes
    const submissionData = { 
      name, 
      label, 
      unit, 
      type, 
      options, 
      desc, 
      link,
      format: format || mapColumnTypeToFormatType(type)
    };

    // Add lookup configuration if this is a lookup field
    if (options === 'lookup') {
      submissionData.lookupTable = lookupTable;
      submissionData.lookupValueColumn = lookupValueColumn;
      submissionData.lookupDisplayColumn = lookupDisplayColumn;
    }

    // Add format-specific attributes based on format type
    switch (submissionData.format) {
      case 'text':
      case 'textarea':
        submissionData.textFormat = textFormat;
        break;
      case 'number':
        submissionData.numberFormat = numberFormat;
        break;
      case 'currency':
        submissionData.currencyFormat = currencyFormat;
        break;
      case 'date':
        submissionData.dateFormat = dateFormat;
        break;
      case 'time':
        submissionData.timeFormat = timeFormat;
        break;
    }

    onSubmit(submissionData);
    
    // Reset form if not editing
    if (!initialData.name) {
        setName('');
        setLabel('');
        setUnit('');
        setType('text');
        setOptions('');
        setDesc('');
        setLink('');
        setFormat('');
        setTextFormat('normal');
        setNumberFormat('0.00');
        setCurrencyFormat('$0,000.00');
        setDateFormat('DD/MM/YYYY');
        setTimeFormat('HH:mm');
        // Reset lookup fields
        setLookupTable('');
        setLookupValueColumn('');
        setLookupDisplayColumn('');
    }
  };

  const handleGenerateDescription = async () => {
    if (!name.trim()) {
      alert('Please enter a column name first');
      return;
    }

    setIsGeneratingDesc(true);
    try {
      const response = await apiFetch('/api/openai/generate-description', {
        method: 'POST',
        body: JSON.stringify({
          columnName: name,
          tableName: tableName,
          columnType: type,
          columnLabel: label
        }),
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response && response.description) {
        setDesc(response.description);
      } else {
        console.error('Invalid response from AI service:', response);
        alert('Failed to generate description: Invalid response from AI service');
      }
    } catch (error) {
      console.error('Error generating description:', error);
      alert(`Failed to generate description: ${error.message}`);
    }
    setIsGeneratingDesc(false);
  };

  // Get format-specific options based on format type
  const getFormatSpecificFields = () => {
    switch (format) {
      case 'text':
      case 'textarea':
        return (
          <div>
            <label>Text Format:</label>
            <select value={textFormat} onChange={e => setTextFormat(e.target.value)}>
              <option value="normal">Normal</option>
              <option value="bold">Bold</option>
              <option value="italic">Italic</option>
              <option value="underline">Underline</option>
            </select>
            <div style={{ fontSize: '0.8em', color: 'gray', marginTop: '5px' }}>
              How text should be formatted in display
            </div>
          </div>
        );
      
      case 'number':
        return (
          <div>
            <label>Number Format:</label>
            <select value={numberFormat} onChange={e => setNumberFormat(e.target.value)}>
              <option value="0">Integer (no decimals)</option>
              <option value="0.0">1 decimal place</option>
              <option value="0.00">2 decimal places</option>
              <option value="0.000">3 decimal places</option>
              <option value="0.0000">4 decimal places (coordinates)</option>
              <option value="0,000">Thousands separator</option>
              <option value="0,000.00">Thousands + 2 decimals</option>
            </select>
            <div style={{ fontSize: '0.8em', color: 'gray', marginTop: '5px' }}>
              How numbers should be displayed (0 = placeholder for digits)
            </div>
          </div>
        );
      
      case 'currency':
        return (
          <div>
            <label>Currency Format:</label>
            <select value={currencyFormat} onChange={e => setCurrencyFormat(e.target.value)}>
              <option value="$0.00">$0.00 (basic)</option>
              <option value="$0,000.00">$0,000.00 (with thousands)</option>
              <option value="$0,000">$0,000 (no cents)</option>
              <option value="USD 0,000.00">USD 0,000.00 (with code)</option>
            </select>
            <div style={{ fontSize: '0.8em', color: 'gray', marginTop: '5px' }}>
              How currency values should be displayed
            </div>
          </div>
        );
      
      case 'date':
        return (
          <div>
            <label>Date Format:</label>
            <select value={dateFormat} onChange={e => setDateFormat(e.target.value)}>
              <option value="DD/MM/YYYY">DD/MM/YYYY (31/12/2023)</option>
              <option value="MM/DD/YYYY">MM/DD/YYYY (12/31/2023)</option>
              <option value="YYYY-MM-DD">YYYY-MM-DD (2023-12-31)</option>
              <option value="DD-MMM-YYYY">DD-MMM-YYYY (31-Dec-2023)</option>
            </select>
            <div style={{ fontSize: '0.8em', color: 'gray', marginTop: '5px' }}>
              How dates should be displayed
            </div>
          </div>
        );
      
      case 'time':
        return (
          <div>
            <label>Time Format:</label>
            <select value={timeFormat} onChange={e => setTimeFormat(e.target.value)}>
              <option value="HH:mm">HH:mm (time only)</option>
              <option value="HH:mm:ss">HH:mm:ss (with seconds)</option>
              <option value="DD/MM/YYYY HH:mm">DD/MM/YYYY HH:mm (date and time)</option>
              <option value="YYYY-MM-DD HH:mm:ss">YYYY-MM-DD HH:mm:ss (ISO format)</option>
            </select>
            <div style={{ fontSize: '0.8em', color: 'gray', marginTop: '5px' }}>
              How time/datetime values should be displayed
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <h3>{initialData.name ? 'Edit' : 'Add New'} Column</h3>
      
      <div>
        <label>Name (unique identifier):</label>
        <input type="text" value={name} onChange={e => setName(e.target.value)} required disabled={!!initialData.name} />
      </div>
      
      <div>
        <label>Label (display name):</label>
        <input type="text" value={label} onChange={e => setLabel(e.target.value)} required />
      </div>
      
      <div>
        <label>Unit:</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <input 
            type="text" 
            value={unit} 
            onChange={e => setUnit(e.target.value)}
            placeholder="Enter unit or type 'global'"
            style={{ flex: 1 }}
          />
          {detectedCategory && (
            <button 
              type="button" 
              onClick={() => setUnit('global')}
              style={{ 
                padding: '5px 10px', 
                backgroundColor: unit.toLowerCase() === 'global' ? '#28a745' : '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px'
              }}
              title={`Use global ${detectedCategory} unit: ${globalSettings[detectedCategory] || 'not set'}`}
            >
              🌍 Global {detectedCategory}
            </button>
          )}
        </div>
        <div style={{ fontSize: '0.8em', color: 'gray', marginTop: '5px' }}>
          {unit.toLowerCase() === 'global' && detectedCategory ? (
            <span>
              Will use global <strong>{detectedCategory}</strong> unit: 
              <strong> {globalSettings[detectedCategory] || 'not configured'}</strong>
            </span>
          ) : detectedCategory ? (
            <span>
              💡 Detected as <strong>{detectedCategory}</strong> field. 
              Click "Global {detectedCategory}" to use system-wide setting.
            </span>
          ) : (
            <span>
              Enter a specific unit (e.g., "m", "ft", "°C") or "global" to use system setting
            </span>
          )}
        </div>
      </div>
      
      <div>
        <label>Type:</label>
        <select value={type} onChange={e => setType(e.target.value)}>
          {/* Text Input Types */}
          <optgroup label="Text Input Types">
            <option value="text">Text (basic text input)</option>
            <option value="string">String (legacy text type)</option>
            <option value="textarea">Text Area (multi-line text)</option>
            <option value="textselectable">Text with Auto-complete (selectable)</option>
            <option value="selectable">Selectable (dropdown with auto-complete)</option>
          </optgroup>
          
          {/* Date & Time Types */}
          <optgroup label="Date & Time Types">
            <option value="date">Date (DD-MMM-YYYY)</option>
            <option value="datetime">DateTime (formats as DD-MMM-YYYY)</option>
          </optgroup>
          
          {/* Number Types */}
          <optgroup label="Number Types">
            <option value="int">Integer (whole numbers)</option>
            <option value="float">Float (decimal numbers)</option>
            <option value="decimal2">Decimal (2 decimal places)</option>
            <option value="decimal3">Decimal (3 decimal places)</option>
            <option value="decimal4">Decimal (4 decimal places)</option>
            <option value="comma">Number with Comma formatting</option>
            <option value="currency">Currency (with symbol)</option>
          </optgroup>
          
          {/* Other Types */}
          <optgroup label="Other Types">
            <option value="boolean">Boolean (Yes/No)</option>
          </optgroup>
        </select>
      </div>

      {/* Format Configuration Section */}
      <div style={{ border: '1px solid #e0e0e0', borderRadius: '6px', padding: '16px', margin: '16px 0', backgroundColor: '#f8f9fa' }}>
        <h4 style={{ margin: '0 0 12px 0', color: '#495057' }}>🎨 Display Format Configuration</h4>
        
        <div>
          <label>Display Format:</label>
          <select value={format} onChange={e => setFormat(e.target.value)}>
            <option value="text">Text</option>
            <option value="textarea">Long Text/Textarea</option>
            <option value="number">Number</option>
            <option value="currency">Currency</option>
            <option value="date">Date</option>
            <option value="time">Time/DateTime</option>
          </select>
          <div style={{ fontSize: '0.8em', color: 'gray', marginTop: '5px' }}>
            How this field should be formatted for display (auto-detected from type: {mapColumnTypeToFormatType(type)})
          </div>
        </div>

        {/* Format-specific configuration fields */}
        {getFormatSpecificFields()}
      </div>
      
      <div>
        <label>Input Options:</label>
        <select value={options} onChange={e => setOptions(e.target.value)}>
          {FIELD_INPUT_OPTIONS.map(option => (
            <option key={option.value || 'none'} value={option.value || ''}>
              {option.label}
            </option>
          ))}
        </select>
        <div style={{ fontSize: '0.8em', color: 'gray', marginTop: '5px' }}>
          Controls how the field behaves in forms (dropdown, lookup, etc.)
        </div>
      </div>
      
      {/* Lookup Configuration Section */}
      {options === 'lookup' && (
        <div style={{ border: '1px solid #e0e0e0', borderRadius: '6px', padding: '16px', margin: '16px 0', backgroundColor: '#f0f8ff' }}>
          <h4 style={{ margin: '0 0 12px 0', color: '#495057' }}>🔗 Lookup Field Configuration</h4>
          
          <div style={{ marginBottom: '12px' }}>
            <label>Lookup Table:</label>
            <select 
              value={lookupTable} 
              onChange={e => setLookupTable(e.target.value)}
              disabled={isLoadingTables}
            >
              <option value="">Select a table...</option>
              {availableTables.map(table => (
                <option key={table.TABLE_NAME} value={table.TABLE_NAME}>
                  {table.TABLE_NAME} ({table.COLUMN_COUNT} columns)
                </option>
              ))}
            </select>
            {isLoadingTables && <span style={{ marginLeft: '8px', color: '#6c757d' }}>Loading tables...</span>}
            <div style={{ fontSize: '0.8em', color: 'gray', marginTop: '5px' }}>
              The database table to lookup values from (e.g., JobCode, WellStatus)
            </div>
          </div>

          <div style={{ marginBottom: '12px' }}>
            <label>Value Column:</label>
            <select 
              value={lookupValueColumn} 
              onChange={e => setLookupValueColumn(e.target.value)}
              disabled={!lookupTable || isLoadingColumns}
            >
              <option value="">Select value column...</option>
              {availableColumns.map(column => (
                <option key={column.COLUMN_NAME} value={column.COLUMN_NAME}>
                  {column.COLUMN_NAME} ({column.DATA_TYPE})
                </option>
              ))}
            </select>
            {isLoadingColumns && <span style={{ marginLeft: '8px', color: '#6c757d' }}>Loading columns...</span>}
            <div style={{ fontSize: '0.8em', color: 'gray', marginTop: '5px' }}>
              The column that contains the actual value to be stored (e.g., JobCode, StatusCode)
            </div>
          </div>

          <div style={{ marginBottom: '12px' }}>
            <label>Display Column:</label>
            <select 
              value={lookupDisplayColumn} 
              onChange={e => setLookupDisplayColumn(e.target.value)}
              disabled={!lookupTable || isLoadingColumns}
            >
              <option value="">Select display column...</option>
              {availableColumns.map(column => (
                <option key={column.COLUMN_NAME} value={column.COLUMN_NAME}>
                  {column.COLUMN_NAME} ({column.DATA_TYPE})
                </option>
              ))}
            </select>
            <div style={{ fontSize: '0.8em', color: 'gray', marginTop: '5px' }}>
              The column that contains the user-friendly description (e.g., JobDescription, StatusName)
            </div>
          </div>

          {lookupTable && lookupValueColumn && lookupDisplayColumn && (
            <div style={{ padding: '8px 12px', backgroundColor: '#d4edda', border: '1px solid #c3e6cb', borderRadius: '4px', marginTop: '12px' }}>
              <strong>📋 Lookup Configuration Preview:</strong><br />
              <span style={{ fontSize: '0.9em' }}>
                Table: <code>{lookupTable}</code><br />
                Value: <code>{lookupValueColumn}</code> → Display: <code>{lookupDisplayColumn}</code><br />
                <em>Forms will show a dropdown with {lookupDisplayColumn} labels, but store {lookupValueColumn} values.</em>
              </span>
            </div>
          )}
        </div>
      )}
      
      <div>
        <label>Description:</label>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
          <textarea 
            value={desc} 
            onChange={e => setDesc(e.target.value)} 
            style={{ flex: 1, minHeight: '60px' }}
            placeholder="Enter description or click 'Generate with AI' to auto-generate"
          />
          <button 
            type="button" 
            onClick={handleGenerateDescription}
            disabled={isGeneratingDesc || !name.trim()}
            style={{ 
              padding: '8px 12px', 
              backgroundColor: isGeneratingDesc ? '#ccc' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isGeneratingDesc || !name.trim() ? 'not-allowed' : 'pointer',
              minWidth: '120px',
              height: 'fit-content'
            }}
          >
            {isGeneratingDesc ? 'Generating...' : '🤖 Generate with AI'}
          </button>
        </div>
        <div style={{ fontSize: '0.8em', color: 'gray', marginTop: '5px' }}>
          AI will generate a description based on column name "{name}" in table "{tableName}"
        </div>
      </div>
      
      <div>
        <label>Link (optional, e.g., /detail/:idField):</label>
        <input type="text" value={link} onChange={e => setLink(e.target.value)} />
      </div>
      
      <button type="submit">{initialData.name ? 'Update' : 'Add'} Column</button>
    </form>
  );
};

export default ColumnForm; 